import React, { useState, useEffect, useCallback, useRef } from 'react';
import usePageTitle from '@/hooks/usePageTitle';
import axios from 'axios';
import { toast } from 'sonner';
import { Eye, EyeOff, RotateCcw, Monitor, ChevronDown, ChevronUp, Volume2, VolumeX, Radio, Square } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PresentationsPage() {
  // Page title
  usePageTitle('Presentations');
  
  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetitionId, setSelectedCompetitionId] = useState('');
  const [categories, setCategories] = useState([]);
  const [teamCategories, setTeamCategories] = useState([]);
  const [presentationState, setPresentationState] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Audio ref for podium music
  const audioRef = useRef(null);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const apparatusOptions = [
    { value: 'vault', label: 'Vault', short: 'VT' },
    { value: 'bars', label: 'Bars', short: 'UB' },
    { value: 'beam', label: 'Beam', short: 'BB' },
    { value: 'floor', label: 'Floor', short: 'FX' },
    { value: 'range', label: 'Range', short: 'RG' },
    { value: 'range', label: 'R&C', short: 'RC' },
    { value: 'aa', label: 'All Around', short: 'AA' },
    { value: 'team', label: 'Team Competition', short: 'TEAM' },
  ];

  // Load competitions
  useEffect(() => {
    const loadCompetitions = async () => {
      try {
        const response = await axios.get(`${API}/public/competitions`);
        const comps = response.data.competitions || response.data || [];
        setCompetitions(comps);
        if (comps.length === 1) {
          setSelectedCompetitionId(comps[0].id);
        }
      } catch (error) {
        console.error('Error loading competitions:', error);
        toast.error('Failed to load competitions');
      }
    };
    loadCompetitions();
  }, []);

  // Load categories when competition selected
  useEffect(() => {
    if (!selectedCompetitionId) return;

    const loadCategories = async () => {
      try {
        const response = await axios.get(`${API}/presentation/categories/${selectedCompetitionId}`);
        if (response.data?.categories) {
          setCategories(response.data.categories.sort());
        }
        if (response.data?.teamCategories) {
          setTeamCategories(response.data.teamCategories);
        }
      } catch (error) {
        console.error('Error loading categories:', error);
        // Fallback: try to get categories from competition
        try {
          const compResponse = await axios.get(`${API}/public/competitions`);
          const competitions = compResponse.data.competitions || compResponse.data || [];
          const comp = competitions.find(c => c.id === selectedCompetitionId);
          if (comp?.categories && comp.categories.length > 0) {
            setCategories(comp.categories.sort());
          }
        } catch (fallbackError) {
          console.error('Fallback also failed:', fallbackError);
        }
      }
    };
    loadCategories();
  }, [selectedCompetitionId]);

  // Poll for presentation state
  useEffect(() => {
    if (!selectedCompetitionId) return;

    const pollState = async () => {
      try {
        const response = await axios.get(`${API}/presentation/state/${selectedCompetitionId}`);
        setPresentationState(prev => {
          const newState = response.data;
          if (JSON.stringify(prev) === JSON.stringify(newState)) return prev;
          return newState;
        });
      } catch (error) {
        // Ignore
      }
    };

    pollState();
    const interval = setInterval(pollState, 500);
    return () => clearInterval(interval);
  }, [selectedCompetitionId]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Load presentation for a specific category/apparatus
  const loadPresentation = async (category, apparatus) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/presentation/setup`, {
        competitionId: selectedCompetitionId,
        category,
        apparatus
      });
      setPresentationState(response.data.state);
      
      const rankingsCount = response.data.state?.rankings?.length || 0;
      if (rankingsCount === 0) {
        toast.info(`No rankings found for ${category} - ${apparatus.toUpperCase()}`);
      } else {
        toast.success(`Loaded ${rankingsCount} ranking(s) for ${category} - ${apparatus.toUpperCase()}`);
      }
    } catch (error) {
      console.error('Error loading presentation:', error);
      toast.error('Failed to load rankings - check if scores exist for this category');
    } finally {
      setLoading(false);
    }
  };

  // Play podium music for AA 1st place reveals
  const playPodiumMusic = async () => {
    try {
      const response = await axios.get(`${API}/presentation/podium-music`);
      const track = response.data.track;
      
      if (track?.fileUrl) {
        // Stop any currently playing track
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
        
        // Create new audio element
        const audio = new Audio();
        audioRef.current = audio;
        setCurrentTrack(track);
        
        // Set up event handlers BEFORE setting src
        audio.onplay = () => {
          console.log('[Podium Music] Playing:', track.name);
          setIsPlaying(true);
        };
        
        audio.onended = () => {
          console.log('[Podium Music] Ended');
          setIsPlaying(false);
          setCurrentTrack(null);
        };
        
        audio.onerror = (e) => {
          console.error('[Podium Music] Error:', e);
          toast.error(`Failed to play: ${track.name}`);
          setIsPlaying(false);
          setCurrentTrack(null);
        };
        
        audio.oncanplaythrough = () => {
          console.log('[Podium Music] Ready to play');
        };
        
        // Set source and load
        audio.src = track.fileUrl;
        audio.load();
        
        // Try to play with proper error handling
        try {
          await audio.play();
          toast.success(`🎵 Playing: ${track.name}`);
        } catch (playError) {
          console.error('[Podium Music] Play failed:', playError);
          toast.error(`Playback blocked - click anywhere first, then try again`);
          setIsPlaying(false);
          setCurrentTrack(null);
        }
      }
    } catch (error) {
      console.error('Failed to get podium music:', error);
      toast.error('Failed to load podium music');
    }
  };

  const stopPodiumMusic = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      setCurrentTrack(null);
    }
  };

  const revealPlace = async (rank) => {
    try {
      await axios.post(`${API}/presentation/reveal`, {
        competitionId: selectedCompetitionId,
        rank
      });
      
      // If revealing 1st place for AA, play podium music after 1.5s delay
      if (rank === 1 && presentationState?.apparatus === 'aa') {
        setTimeout(() => {
          playPodiumMusic();
        }, 1500);
      }
    } catch (error) {
      toast.error('Failed to reveal');
    }
  };

  const hidePlace = async (rank) => {
    try {
      await axios.post(`${API}/presentation/hide`, {
        competitionId: selectedCompetitionId,
        rank
      });
    } catch (error) {
      toast.error('Failed to hide');
    }
  };

  const resetPresentation = async () => {
    try {
      await axios.post(`${API}/presentation/reset`, {
        competitionId: selectedCompetitionId
      });
    } catch (error) {
      toast.error('Failed to reset');
    }
  };

  const stopPresentation = async () => {
    try {
      await axios.post(`${API}/presentation/stop`, {
        competitionId: selectedCompetitionId
      });
      setPresentationState(null);
    } catch (error) {
      toast.error('Failed to stop');
    }
  };

  // Group rankings by rank
  const getRankingsGroupedByRank = useCallback(() => {
    if (!presentationState?.rankings) return {};
    const grouped = {};
    presentationState.rankings.forEach(r => {
      const rank = r.rank;
      if (rank != null) {
        if (!grouped[rank]) grouped[rank] = [];
        grouped[rank].push(r);
      }
    });
    return grouped;
  }, [presentationState]);

  const groupedRankings = getRankingsGroupedByRank();
  const ranks = Object.keys(groupedRankings).map(Number).sort((a, b) => a - b);
  const revealedPlaces = presentationState?.revealedPlaces || [];

  const isCurrentPresentation = (category, apparatus) => {
    return presentationState?.isActive && 
           presentationState?.category === category && 
           presentationState?.apparatus === apparatus;
  };

  return (
    <div className="min-h-screen bg-white text-black flex flex-col">

      {/* LIVE / IDLE status banner */}
      {presentationState?.isActive ? (
        <div className="bg-red-600 text-white px-6 py-3 flex items-center justify-between flex-shrink-0" data-testid="presentation-live-banner">
          <div className="flex items-center gap-3">
            <Radio className="w-5 h-5 animate-pulse" />
            <span className="font-bold text-lg">PRESENTATION LIVE</span>
            <span className="text-red-200 text-sm">
              {presentationState.category} — {apparatusOptions.find(a => a.value === presentationState.apparatus)?.label || presentationState.apparatus?.toUpperCase()}
            </span>
          </div>
          <button
            onClick={stopPresentation}
            className="flex items-center gap-2 px-5 py-2 bg-white text-red-600 hover:bg-red-50 rounded-lg font-bold text-sm transition-all"
            data-testid="stop-presentation-main-btn"
          >
            <Square className="w-4 h-4 fill-red-600" />
            Stop Presentation — Resume Normal Display
          </button>
        </div>
      ) : (
        <div className="bg-gray-100 text-gray-600 px-6 py-2 flex items-center gap-2 text-sm flex-shrink-0" data-testid="presentation-idle-banner">
          <div className="w-2 h-2 rounded-full bg-gray-400" />
          <span>Display is in <strong>normal scores mode</strong>. Select a category below and click <strong>Start Presenting</strong> to go live.</span>
        </div>
      )}

      {/* Header */}
      <div className="border-b border-gray-200 p-4 flex-shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/scorz-logo.png" alt="Scorz" className="h-10" />
            <h1 className="text-xl font-bold">Presentations</h1>
            {/* Music indicator with prominent stop button */}
            {isPlaying && currentTrack && (
              <div className="flex items-center gap-3 px-4 py-2 bg-green-100 text-green-800 rounded-lg">
                <Volume2 className="w-5 h-5 animate-pulse" />
                <span className="font-medium">🎵 {currentTrack.name}</span>
                <button 
                  onClick={stopPodiumMusic}
                  className="flex items-center gap-1 px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors font-medium text-sm"
                >
                  <VolumeX className="w-4 h-4" />
                  Stop Music
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-4">
            <Select value={selectedCompetitionId} onValueChange={setSelectedCompetitionId}>
              <SelectTrigger className="w-64 bg-white border-gray-300">
                <SelectValue placeholder="Select competition" />
              </SelectTrigger>
              <SelectContent>
                {competitions.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <a 
              href="/display" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              <Monitor className="w-4 h-4" />
              Display
            </a>
          </div>
        </div>
      </div>

      <div className="flex-1 flex max-w-7xl mx-auto w-full p-4 gap-4">
        {/* Left: Categories */}
        <div className="w-1/3 space-y-2 overflow-y-auto">
          <h2 className="text-lg font-semibold mb-3">Categories</h2>
          {selectedCompetitionId && (categories.length > 0 || teamCategories.length > 0) ? (
            <>
              {categories.map(category => (
              <div key={category} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
                  className="w-full p-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <span className="font-medium">{category}</span>
                  {expandedCategory === category ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>

                {expandedCategory === category && (
                  <div className="p-3 bg-white border-t border-gray-200">
                    <div className="grid grid-cols-4 gap-2">
                      {apparatusOptions.filter(a => a.value !== 'team').map(app => {
                        const isCurrent = isCurrentPresentation(category, app.value);
                        return (
                          <button
                            key={app.value}
                            onClick={() => loadPresentation(category, app.value)}
                            disabled={loading}
                            data-testid={`start-presentation-${category}-${app.value}`}
                            className={`p-2 rounded border text-center transition-all ${
                              isCurrent
                                ? 'bg-red-600 text-white border-red-600 ring-2 ring-red-300'
                                : 'bg-white text-black border-gray-300 hover:border-black hover:bg-gray-50'
                            }`}
                          >
                            <div className="text-lg font-bold">{app.short}</div>
                            <div className="text-xs mt-1">{isCurrent ? '● LIVE' : 'Start'}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}

              {teamCategories.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1">Team Categories</div>
                  {teamCategories.map(teamCat => {
                    const key = `team::${teamCat}`;
                    const isCurrent = isCurrentPresentation(teamCat, 'team');
                    return (
                      <div key={key} className="border border-blue-200 rounded-lg overflow-hidden mb-2">
                        <button
                          onClick={() => setExpandedCategory(expandedCategory === key ? null : key)}
                          className="w-full p-3 flex items-center justify-between bg-blue-50 hover:bg-blue-100 transition-colors"
                        >
                          <span className="font-medium text-blue-800">{teamCat}</span>
                          {expandedCategory === key ? (
                            <ChevronUp className="w-4 h-4 text-blue-600" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-blue-600" />
                          )}
                        </button>
                        {expandedCategory === key && (
                          <div className="p-3 bg-white border-t border-blue-200">
                            <button
                              onClick={() => loadPresentation(teamCat, 'team')}
                              disabled={loading}
                              data-testid={`start-presentation-${teamCat}-team`}
                              className={`w-full p-3 rounded border text-center transition-all ${
                                isCurrent
                                  ? 'bg-red-600 text-white border-red-600 ring-2 ring-red-300'
                                  : 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                              }`}
                            >
                              <div className="text-base font-bold">TEAM</div>
                              <div className="text-xs mt-1">{isCurrent ? '● LIVE' : 'Start Team Presentation'}</div>
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : selectedCompetitionId ? (
            <div className="text-center py-8 text-gray-400">
              <p>No categories found</p>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <p>Select a competition</p>
            </div>
          )}
        </div>

        {/* Right: Active Presentation / Reveal Controls */}
        <div className="flex-1 border border-gray-200 rounded-lg p-4">
          {presentationState?.isActive && ranks.length > 0 ? (
            <>
              {/* Header */}
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                <div>
                  <div className="text-sm text-gray-500">Now Presenting</div>
                  <div className="text-2xl font-bold">
                    {presentationState.category} — {apparatusOptions.find(a => a.value === presentationState.apparatus)?.label}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={resetPresentation} variant="outline" size="sm" className="border-gray-300">
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Reset
                  </Button>
                  <Button
                    onClick={stopPresentation}
                    size="sm"
                    className="bg-red-600 hover:bg-red-700 text-white border-none"
                    data-testid="stop-presentation-reveal-btn"
                  >
                    <Square className="w-4 h-4 mr-1 fill-white" />
                    Stop Presentation
                  </Button>
                </div>
              </div>

              {/* Reveal Grid - Big buttons */}
              <div className="grid grid-cols-3 gap-4">
                {[...ranks].reverse().map(rank => {
                  const gymnasts = groupedRankings[rank] || [];
                  const isRevealed = revealedPlaces.includes(rank);
                  const isJoint = gymnasts.length > 1;

                  return (
                    <button
                      key={rank}
                      onClick={() => isRevealed ? hidePlace(rank) : revealPlace(rank)}
                      className={`p-6 rounded-xl border-2 transition-all ${
                        isRevealed 
                          ? 'bg-black text-white border-black' 
                          : 'bg-white text-black border-gray-300 hover:border-black hover:shadow-lg'
                      }`}
                    >
                      <div className="text-5xl font-black mb-2">{rank}</div>
                      <div className="text-sm truncate font-medium">
                        {isJoint ? `Joint (${gymnasts.length})` : gymnasts[0]?.name || 'Unknown'}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {!isJoint && gymnasts[0]?.club}
                      </div>
                      <div className="mt-3 flex items-center justify-center gap-2">
                        {isRevealed ? (
                          <>
                            <EyeOff className="w-5 h-5" />
                            <span className="text-sm">Hide</span>
                          </>
                        ) : (
                          <>
                            <Eye className="w-5 h-5" />
                            <span className="text-sm">Reveal</span>
                          </>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          ) : presentationState?.isActive ? (
            <div className="h-full flex flex-col items-center justify-center text-gray-400">
              <img src="/scorz-logo.png" alt="Scorz" className="h-16 mb-4 opacity-30" />
              <p className="text-lg">No rankings found for this selection</p>
              <p className="text-sm">Try a different category or apparatus</p>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-gray-400">
              <img src="/scorz-logo.png" alt="Scorz" className="h-20 mb-4 opacity-30" />
              <p className="text-xl font-medium mb-2">Select a Presentation</p>
              <p className="text-sm">Choose a category and apparatus from the left panel</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
